import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Box, Button, Grid, TextField } from '@mui/material';
import { useAuthContext } from 'src/components/Context/AuthContext/AuthContext';

import { url } from 'src/Helpers/utils/Constant';
import {
  IpaymentableItem,
  IuserMemberList,
} from 'src/Types/module/invoice/invoiceTypes';
import { useFieldArray, useForm } from 'react-hook-form';
import { HttpClient } from 'src/Helpers/http/http';
import Toaster from 'src/Toaster/Toaster';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import moment from 'moment';
import { removeUndefinedAndNull } from 'src/common/removeUndefinedAndNull';
import DynamicOldMoneyReceipt from './DynamicOldMoneyReceipt';

const CreateOldMoneyReceipt = ({ handleClose, setRefresh }) => {
  const { memberId } = useParams();

  const { user } = useAuthContext();
  const [allPaymentableItem, setAllPaymentableItem] = useState<
    IpaymentableItem[]
  >([]);
  const [userMember, setUserMember] = useState<IuserMemberList[]>([]);
  const [_isLoading, setIsLoading] = useState<Boolean>(false);
  const { register, handleSubmit, control, watch, setValue } = useForm();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'items',
  });
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const items = watch('items');
  const totalItemAmount = items?.reduce(
    (sum, item) => sum + Number(item.collected_amount || 0),
    0
  );

  if (fields.length === 0) {
    append({ id: '', collected_amount: '', year: '', cheque_no: '' });
  }
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.user_admin_id) return;

      setIsLoading(true);

      try {
        const urlList = [`${url}/api/atab/payment/get/all/paymentable/item`];
        const responses = await Promise.all(
          urlList.map((u) => fetch(u).then((r) => r.json()))
        );
        const result = responses[0];
        if (result?.data) {
          setAllPaymentableItem(result.data);
        }
      } catch (error) {
        console.error('Error fetching paymentable items:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [user?.user_admin_id]);

  const onSubmit = async (submitData: any) => {
    setLoading(true);
    submitData.items.forEach((item) => {
      item.collected_amount = Number(item.collected_amount);
      if (item.year.length) {
        item.year = item.year.map((year) => year.value).join('-');
      } else {
        item.year = `${moment().year()}`;
      }
    });
    try {
      const res: any = await HttpClient.post(
        `${url}/api/atab/admin/money-receipt/old`,
        removeUndefinedAndNull({
          ...submitData,
          user_member_id: memberId,
          total_collected_amount: totalItemAmount,
        })
      );
      if (res.success) {
        Toaster().fire({
          icon: 'success',
          title: res.message,
        });
        setLoading(false);
        handleClose();
        setRefresh((prv) => !prv);
      } else {
        setLoading(false);
      }
    } catch (err: any) {
      Toaster().fire({
        icon: 'error',
        title: `${err.response?.data?.message}`,
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Atab - Create Old Money Receipt</title>
      </Helmet>

      <Box
        sx={{
          mx: 4,
          mt: 2,
          maxWidth: '800px',
          margin: '0 auto',
        }}
        component='form'
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} md={12} lg={6}>
            <TextField
              fullWidth
              required
              id='payment_date'
              label='Money Receipt Date'
              type='date'
              InputLabelProps={{ shrink: true }}
              variant='outlined'
              {...register('payment_date')}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <TextField
              fullWidth
              required
              id='money_receipt_no'
              label='Money Receipt No'
              type='text'
              variant='outlined'
              {...register('money_receipt_no')}
            />
          </Grid>
          <Grid item xs={12} md={12} lg={6}>
            <TextField
              fullWidth
              required
              id='voucher_no'
              label='Voucher No'
              type='text'
              variant='outlined'
              {...register('voucher_no')}
            />
          </Grid>
          <Grid item xs={12}>
            {/* Expense Items */}
            {fields?.map((item, index) => {
              return (
                <DynamicOldMoneyReceipt
                  register={register}
                  allPaymentableItem={allPaymentableItem}
                  item={item}
                  index={index}
                  key={index}
                  control={control}
                  remove={remove}
                  fields={fields}
                  setValue={setValue}
                  memberList={userMember}
                />
              );
            })}
          </Grid>
          <Grid item xs={12}>
            <Button
              type='button'
              variant='outlined'
              onClick={() => append({ id: '', amount: '', year: '' })}
            >
              Add Item
            </Button>
          </Grid>
        </Grid>

        <Grid sx={{ mt: 2 }} item xs={12} md={12} lg={12}>
          <TextField
            fullWidth
            required
            rows={3}
            multiline
            id='description'
            label='Description'
            type='text'
            variant='outlined'
            {...register('description')}
          />
        </Grid>
        <p style={{ fontSize: '14px', fontWeight: 'bold' }}>
          Total Invoice Amount : {totalItemAmount}
        </p>
        <Box textAlign={'center'} mt={3}>
          {loading ? (
            <LoadingButton
              loading={loading}
              fullWidth
              color='secondary'
              disableElevation
              sx={{
                mt: 3,
                mb: 2,
                py: 1,
              }}
              variant='contained'
            >
              Loading...
            </LoadingButton>
          ) : (
            <Button
              type='submit'
              variant='contained'
              disableElevation
              sx={{
                mt: 3,
                mb: 2,
                py: 1,
              }}
            >
              Create
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default CreateOldMoneyReceipt;
